import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AppSettings, SettingsSection } from '@ace-tracker/shared';
import { saveSettingsSection } from '../../api';
import { SETTINGS_QUERY_KEY } from '../../settings';

/**
 * Saves one settings section, then refreshes everything that depends on it:
 * the shared settings cache (display names) and every dashboard query
 * (costs and developer exclusions are applied server-side).
 */
export function useSaveSettings<S extends SettingsSection>(section: S) {
  const queryClient = useQueryClient();
  const [savedAt, setSavedAt] = useState<number>();
  const mutation = useMutation({
    mutationFn: (value: AppSettings[S]) => saveSettingsSection(section, value),
    onSuccess: (saved) => {
      queryClient.setQueryData(SETTINGS_QUERY_KEY, saved);
      void queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-overview'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-executive'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-health'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-spend'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-efficiency'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-sessions'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-budget'] });
      void queryClient.invalidateQueries({ queryKey: ['price-feed'] });
      void queryClient.invalidateQueries({ queryKey: ['settings-history'] });
      setSavedAt(Date.now());
    },
  });
  return {
    save: (value: AppSettings[S]) => {
      setSavedAt(undefined);
      mutation.mutate(value);
    },
    saving: mutation.isPending,
    error: mutation.error ? (mutation.error as Error).message : undefined,
    savedAt,
    reset: () => {
      mutation.reset();
      setSavedAt(undefined);
    },
  };
}
